/**
 * Which optional decklist row columns to hide at a given row-list width.
 *
 * A decklist row is one flex line: quantity, name, then the optional columns (printing
 * kebab, badges, rarity, set code, price, mana cost). As the row list narrows, optional
 * columns drop out so the card name keeps its 6ch floor. Quantity, name, the kebab
 * and the row action never hide.
 *
 * This is content-aware: only columns the row actually renders are budgeted. (The old
 * CSS container queries assumed every toggle was on, so the price hid below 290px even
 * when it was the only optional column; deck-view columns are 240-330px wide.)
 *
 * Widths are measured from the rendered markup (Inter, 2026-10-09):
 *   regular rows (text-base 13px, gap-x-1.5 = 6px): qty 20 (min-w-5), kebab 20 (w-5),
 *     GC badge 21, rarity 8 (one letter), set code 20 (three letters), price 48
 *     (min-w-[3rem]), mana 61 (three symbols at ~20 each).
 *   compact rows (text-xs 11px, gap-x-1 = 4px): qty 16 (min-w-4), the same badge,
 *     rarity, set and kebab widths; compact rows never render price or mana.
 * The name floor is min-w-[6ch], five letters plus the ellipsis: 6 x 8.2 = 49px at 13px,
 * taken as 50; 6 x 6.9 = 41px at 11px, taken as 42.
 *
 * Badges vary per row (GC, Banned, Unreleased, the in-deck check). The budget covers the
 * common GC badge only; heavier rows (the 58px Unreleased badge, five-symbol costs) wrap
 * their trailing columns onto a second line (the row is flex-wrap) instead of overflowing.
 */

export type OptionalColumn = 'rarity' | 'set' | 'badges' | 'price' | 'mana';

/** The user-toggled columns, plus whether rows carry the printing kebab. */
export interface EnabledColumns {
  price: boolean;
  rarity: boolean;
  set: boolean;
  kebab: boolean;
  /** The row carries a labelled action button (never hides). */
  action: boolean;
}

/**
 * Hide order: rarity and set go first, then badges; the price is user-chosen money info,
 * so it outlives them. Mana goes last.
 */
export const HIDE_ORDER: readonly OptionalColumn[] = ['rarity', 'set', 'badges', 'price', 'mana'];

interface RowMetrics {
  gap: number;
  qty: number;
  nameFloor: number;
  kebab: number;
  /** The labelled row action ("Make room", ~12 chars at text-xs). */
  action: number;
  columns: Record<OptionalColumn, number>;
}

export const REGULAR_ROW: RowMetrics = {
  gap: 6,
  qty: 20,
  nameFloor: 50,
  kebab: 20,
  action: 84,
  columns: { badges: 21, rarity: 8, set: 20, price: 48, mana: 61 },
};

export const COMPACT_ROW: RowMetrics = {
  gap: 4,
  qty: 16,
  nameFloor: 42,
  kebab: 20,
  action: 84,
  columns: { badges: 21, rarity: 8, set: 20, price: 48, mana: 61 },
};

/** The optional columns a row renders: badges always may; compact rows have no price or mana. */
function presentColumns(enabled: EnabledColumns, compact: boolean): OptionalColumn[] {
  return HIDE_ORDER.filter(col => {
    switch (col) {
      case 'rarity': return enabled.rarity;
      case 'set': return enabled.set;
      case 'badges': return true;
      case 'price': return enabled.price && !compact;
      case 'mana': return !compact;
    }
  });
}

/**
 * The columns to hide so a row of `width` px keeps the name at its floor. Columns drop in
 * HIDE_ORDER until the row fits; a column that is not rendered is never reported.
 */
export function hiddenColumns(width: number, enabled: EnabledColumns, compact: boolean): Set<OptionalColumn> {
  const m = compact ? COMPACT_ROW : REGULAR_ROW;
  const shown = presentColumns(enabled, compact);
  // Items on the line: qty, name, the kebab when present, then each shown column; a gap between each pair.
  const fixedItems = 2 + (enabled.kebab ? 1 : 0) + (enabled.action ? 1 : 0);
  let needed = m.qty + m.nameFloor + (enabled.kebab ? m.kebab : 0) + (enabled.action ? m.action : 0) + (fixedItems - 1) * m.gap;
  for (const col of shown) needed += m.columns[col] + m.gap;

  const hidden = new Set<OptionalColumn>();
  for (const col of shown) {
    if (needed <= width) break;
    hidden.add(col);
    needed -= m.columns[col] + m.gap;
  }
  return hidden;
}
